import type { Currency } from "@utils/discountUtils";
import { gql } from "graphql-request";

export type AppliedDiscount = {
  discount_amount: number;
  description: string;
  coupon_id: string;
  discount_strategy: string;
};

export type CartInfoItem = {
  product_title: string;
  product_description: string;
  sku: string;
  frequency?: string;
  quantity: number;
  price_amount: number;
  unit_amount: number;
  type: "digitalProduct" | "subscription" | "physicalProduct" | "promo" | "shipment";
  product_categories: string[];
  accessory_categories: string[];
};

export type CartInfo = {
  currency: Currency;
  subtotal: number;
  total_amount: number;
  applied_discounts: AppliedDiscount[];
  tax: number | undefined;
  items?: CartInfoItem[];
  bundles?: CartInfoItem[];
};

export type PurchaseItem = {
  sku: string;
  quantity?: number;
};

type GetCartInfoHeaders = {
  "Content-Type": string;
  Authorization?: string;
};

export type CartInfoParams = {
  items: PurchaseItem[];
  purchase_type: string;
  currency?: string;
};

export type GetCartInfoRequest = {
  params: CartInfoParams;
  authToken: string | null;
};

export type GetCartInfoResponse = {
  data: CartInfo;
};

const GET_CART_INFO = gql`
  query GET_CART_INFO($params: CartInfoParams!) {
    get_cart_info(params: $params) {
      currency
      subtotal
      total_amount
      applied_discounts {
        discount_amount
        description
        coupon_id
        discount_strategy
      }
      tax
      items {
        product_title
        product_description
        sku
        frequency
        quantity
        price_amount
        unit_amount
        type
        product_categories
        accessory_categories
      }
      bundles {
        product_title
        product_description
        sku
        frequency
        quantity
        price_amount
        unit_amount
        type
        product_categories
        accessory_categories
      }
    }
  }
`;

export const getCartInfo = async ({
  params,
  authToken,
}: GetCartInfoRequest): Promise<GetCartInfoResponse> => {
  const variables = {
    params,
  };

  const headers: GetCartInfoHeaders = { "Content-Type": "application/json" };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  try {
    const res = await fetch(import.meta.env.VITE_PURCHASE_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        query: GET_CART_INFO,
        variables,
        operationName: "GET_CART_INFO",
      }),
    });

    const json = await res.json();
    if (json.errors) {
      console.error("GraphQL errors:", json.errors);
      throw new Error(json.errors[0].message || "Failed to fetch cart info");
    }

    if (!json.data?.get_cart_info) {
      throw new Error("Invalid response format from get_cart_info");
    }

    const server = json.data.get_cart_info;
    return {
      data: {
        ...server,
        // Ensure structural compatibility with CartInfo type while preserving server data
        items: server.items ?? [],
        bundles: server.bundles ?? [],
      },
    };
  } catch (error) {
    console.error("Get cart info error:", error);
    throw error;
  }
};
